import { useEffect, useState, type FC } from 'react'

import { Input } from '@skytech/ui'
import { getQueryFilter, useDebounce } from '@skytech/utils'

import { OrderSummary } from '@/components/ecom-policy/policy/OrderSummary'
import type { GreenpoolAsset, Policy } from '@/components/ecom-policy/types'
import { useTranslation } from '@/components/i18n/hooks'
import { useSearchParamsByKey } from '@/hooks/searchParams'

import { GreenpoolProduct } from './GreenpoolProduct'

export const GreenpoolProductsGrid: FC<{ data: Policy; showSummary?: boolean }> = ({
  data,
  showSummary = true,
}) => {
  const translate = useTranslation()

  const [querySearch, setSearchQuery] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)
  if (!data.greenpoolTypeId) return null

  const greenpoolAssetsFiltered = data.greenpoolAssets
    .sort((a, b) => a.assetName.localeCompare(b.assetName))
    .filter(
      getQueryFilter(debouncedAssetSearchTerm, (listItem: GreenpoolAsset) => [listItem.assetName])
    )

  useEffect(() => {
    if (searchFilter === debouncedAssetSearchTerm) {
      setSearchFilter(querySearch || '')
    }
  }, [querySearch])

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  return (
    <>
      {(Object.values(greenpoolAssetsFiltered).length > 9 ||
        Boolean(searchFilter || debouncedAssetSearchTerm)) && (
        <Input
          id="search"
          type="text"
          value={searchFilter}
          className="mt-2 py-2 text-base md:py-3"
          placeholder={translate('Type to filter')}
          onChange={e => setSearchFilter(e.target.value)}
        />
      )}

      <ul className="mobile:grid-cols-[repeat(auto-fill,_minmax(15rem,_1fr))] mx-0 my-2 grid list-none grid-cols-[repeat(auto-fill,_minmax(10rem,_1fr))] gap-2 p-0 pb-20 sm:gap-4">
        {Object.values(greenpoolAssetsFiltered).map(asset => (
          <GreenpoolProduct
            key={asset.id}
            {...asset}
            isOnlyProductView={Boolean(data.isLocked && data.isProductViewAllowedWhenLocked)}
            searchQuery={debouncedAssetSearchTerm || ''}
          />
        ))}
      </ul>
      {showSummary && <OrderSummary isFloating />}
    </>
  )
}
